import axios from "./axios";

// Oppdatering / status_update — polymorphic status feed (v1: project, customer).
// Backend: /api/v1/status_updates

/**
 * True when StatusUpdate API routes look undeployed / missing (not a normal
 * business 404 like "Fant ikke tilknyttet objekt").
 */
export function isStatusUpdateModuleUnavailable(errorOrResult) {
  if (!errorOrResult) return false;
  if (errorOrResult.__moduleUnavailable === true) return true;

  const status =
    errorOrResult.__status ??
    errorOrResult.status ??
    errorOrResult.response?.status;
  const data = errorOrResult.response?.data ?? errorOrResult;

  // HTML body ⇒ Rails/nginx did not hit the JSON API (route missing).
  if (typeof data === "string" && /<!DOCTYPE|<html/i.test(data)) return true;

  if (status === 501) return true;

  // 404 with a JSON body ⇒ API route exists (business not-found / empty envelope).
  // Only treat non-JSON 404s (empty body, plain text) as undeployed — HTML already
  // matched above.
  if (status === 404) {
    if (data && typeof data === "object") return false;
    return true;
  }

  return false;
}

function wrapStatusUpdateError(error) {
  const status = error?.response?.status;
  const raw = error?.response?.data;
  const base =
    raw && typeof raw === "object"
      ? raw
      : { errors: [typeof raw === "string" && raw.trim() ? "Request failed" : "Request failed"] };

  return {
    ...base,
    __error: true,
    __status: status,
    __moduleUnavailable: isStatusUpdateModuleUnavailable(error),
  };
}

export async function fetchStatusUpdates({
  subjectType,
  subjectId,
  mine = false,
  q,
  page = 1,
  perPage = 25,
} = {}) {
  const params = { page, per_page: perPage };
  if (mine) {
    params.mine = true;
    if (q != null && String(q).trim() !== "") params.q = String(q).trim();
  } else {
    params.subject_type = subjectType;
    params.subject_id = subjectId;
    if (q != null && String(q).trim() !== "") params.q = String(q).trim();
  }

  return axios({
    method: "get",
    url: "/status_updates",
    params,
  })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

export async function createStatusUpdate(payload) {
  return axios({
    method: "post",
    url: "/status_updates",
    data: { status_update: payload },
  })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

export async function updateStatusUpdate(id, payload) {
  return axios({
    method: "put",
    url: `/status_updates/${id}`,
    data: { status_update: payload },
  })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

export async function deleteStatusUpdate(id) {
  return axios({ method: "delete", url: `/status_updates/${id}` })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

// Standalone voice → text (OpenAI Whisper via promang-api). Not TALAI.
// Returns { text, title, body_html, suggestions?: { todos, projects, users } }.
export async function transcribeAudio(
  blob,
  { language = "no", filename = "voice-note.webm", context } = {}
) {
  const form = new FormData();
  form.append("audio", blob, filename);
  form.append("language", language);
  appendSuggestionContext(form, context);
  return axios({
    method: "post",
    url: "/ai/transcriptions",
    data: form,
    headers: { "Content-Type": "multipart/form-data" },
  })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

// Extract todos / projects / users from typed Oppdatering text. Never creates records.
// Pass acceptedTodoTitles so the API can skip already-created «husk at…» items.
export async function suggestStatusUpdateContent(
  text,
  { acceptedTodoTitles, acceptedHourKeys, context } = {}
) {
  const data = { text };
  const titles = (Array.isArray(acceptedTodoTitles) ? acceptedTodoTitles : [])
    .map((t) => String(t || "").trim())
    .filter(Boolean);
  if (titles.length > 0) {
    data.accepted_todo_titles = titles;
  }
  const hourKeys = (Array.isArray(acceptedHourKeys) ? acceptedHourKeys : [])
    .map((t) => String(t || "").trim())
    .filter(Boolean);
  if (hourKeys.length > 0) {
    data.accepted_hour_keys = hourKeys;
  }
  if (context && typeof context === "object") {
    data.context = context;
  }
  return axios({
    method: "post",
    url: "/ai/status_update_suggestions",
    data,
  })
    .then((response) => response.data)
    .catch((error) => wrapStatusUpdateError(error));
}

const SUGGESTION_CONTEXT_KEYS = [
  "subject_type",
  "subject_id",
  "project_id",
  "project_name",
  "customer_id",
  "customer_name",
];

function appendSuggestionContext(form, context) {
  if (!form || !context || typeof context !== "object") return;
  SUGGESTION_CONTEXT_KEYS.forEach((key) => {
    const value = context[key];
    if (value == null || value === "") return;
    form.append(`context[${key}]`, String(value));
  });
}
